import React from 'react';
import { Alert, Label } from 'reactstrap';
import { FieldArray } from 'formik';
import AddButton from '~/components/addButton/addButton';
import FormikInputField from '~/components/formikInputField/formikInputField';
import FormLayout from '~/components/formLayout/formLayout';
import SubmissionButton from '~/components/submissionButton/submissionButton';
import BeerStyleOptions from '~/containers/beerStyleOptions/beerStyleOptions';
import HSCodeOptions from '~/containers/hsCodeOptions/hsCodeOptions';
import IngredientOptions from '~/containers/ingredientOptions/ingredientOptions';
import VesselTypeOptions from '~/containers/vesselTypeOptions/vesselTypeOptions';
import IconTooltip from '~/components/iconTooltip/iconTooltip';
import { renderError, hasError } from '~/utils/formikValidation';
import styles from './styles.css';

const ProductForm = ({ values, errors, touched, handleSubmit, isSubmitting }) => {
  const { ingredients, variants } = values;
  return (
    <form onSubmit={handleSubmit}>
      {errors.unknown && <Alert color="danger">{errors.unknown}</Alert>}
      <FormLayout title="Overview">
        <div className="mb3">
          <Label>Name</Label>
          <FormikInputField name="name" invalid={!!errors.name} />
          {errors.name && (
            <p data-qa="name-error" className="error">
              {errors.name}
            </p>
          )}
        </div>
        <div className="mb3">
          <Label>Customs name</Label>
          <FormikInputField name="customsName" />
        </div>
        <div className="mb3">
          <Label>Description</Label>
          <FormikInputField type="textarea" name="description" />
        </div>
      </FormLayout>
      <FormLayout className="mt4" title="Detail">
        <div className="mb3">
          <Label>Beer style</Label>
          <FormikInputField type="select" name="beerStyleId" invalid={!!errors.beerStyleId}>
            <BeerStyleOptions />
          </FormikInputField>
          {errors.beerStyleId && (
            <p data-qa="beerStyleId-error" className="error">
              {errors.beerStyleId}
            </p>
          )}
        </div>
        <div className="mb3">
          <Label>HS Code</Label>
          <FormikInputField type="select" name="hsCodeId">
            <HSCodeOptions />
          </FormikInputField>
        </div>
        <div className="mb3">
          <Label>Lot number prefix</Label>
          <FormikInputField name="lotPrefix" invalid={!!errors.lotPrefix} />
          {errors.lotPrefix && (
            <p data-qa="lotPrefix-error" className="error">
              {errors.lotPrefix}
            </p>
          )}
        </div>
        <div className="mb3">
          <Label>Alcohol percent (ABV)</Label>
          <FormikInputField name="abv" step=".01" type="number" invalid={!!errors.abv} />
          {errors.abv && (
            <p data-qa="abv-error" className="error">
              {errors.abv}
            </p>
          )}
        </div>
        <div className="mb3">
          <Label>Ingredient cost per litre ($)</Label>
          <FormikInputField name="costPerLitre" step=".01" type="number" invalid={!!errors.costPerLitre} />
          {errors.costPerLitre && (
            <p data-qa="costPerLitre-error" className="error">
              {errors.costPerLitre}
            </p>
          )}
        </div>
      </FormLayout>
      <FormLayout className="mt4" title="Ingredients">
        <div className="mb3">
          <Label>Volume these ingredients relate to</Label>
          <FormikInputField name="volume" type="number" invalid={!!errors.volume} />
          {errors.volume && (
            <p data-qa="volume-error" className="error">
              {errors.volume}
            </p>
          )}
        </div>
        <div className="mb3">
          <h5 className="f6 mr3 ttu">Ingredients</h5>
          <FieldArray
            name="ingredients"
            validateOnChange={false}
            render={arrayHelpers => (
              <div>
                {ingredients.map((ingredient, idx) => (
                  <div key={idx} className={styles.step}>
                    <div className="flex justify-end">
                      {ingredients.length > 1 && (
                        <div className="flex">
                          <IconTooltip
                            icon="delete-circle"
                            text="Remove ingredient"
                            className="ml3 stroke-brand-red"
                            onClick={() => arrayHelpers.remove(idx)}
                          />
                        </div>
                      )}
                    </div>
                    <div className="mh3">
                      <div className="mb3">
                        <Label>Ingredient</Label>
                        <FormikInputField
                          type="select"
                          name={`ingredients.${idx}.ingredientId`}
                          invalid={hasError(errors, idx, 'ingredients', 'ingredientId')}
                        >
                          <IngredientOptions />
                        </FormikInputField>
                        {renderError(errors, idx, 'ingredients', 'ingredientId')}
                      </div>
                      <div className="flex flex-column flex-row-ns">
                        <div className="mb3 mr0 mr3-ns flex-grow-1">
                          <Label>What quantity?</Label>
                          <FormikInputField step=".01" type="number" name={`ingredients.${idx}.amount`} />
                          {renderError(errors, idx, 'ingredients', 'amount')}
                        </div>
                        <div className="mb3">
                          <Label>&nbsp;</Label>
                          <FormikInputField
                            type="select"
                            name={`ingredients.${idx}.measurementType`}
                            invalid={hasError(errors, idx, 'ingredients', 'measurementType')}
                          >
                            <option value="KG">Kilograms</option>
                            <option value="G">Grams</option>
                            <option value="L">Litres</option>
                            <option value="ML">Millilitres</option>
                            {renderError(errors, idx, 'ingredients', 'measurementType')}
                          </FormikInputField>
                        </div>
                      </div>
                      <div className="mb3">
                        <Label>Notes</Label>
                        <FormikInputField type="textarea" name={`ingredients.${idx}.notes`} />
                      </div>
                    </div>
                  </div>
                ))}
                <div className="tr">
                  <AddButton
                    color="primary"
                    onClick={() =>
                      arrayHelpers.push({
                        measurementType: 'KG'
                      })
                    }
                  >
                    Add another ingredient
                  </AddButton>
                </div>
              </div>
            )}
          />
        </div>
      </FormLayout>

      <FormLayout className="mt4" title="Variants for sale">
        <div className="mb3">
          <h5 className="f6 mr3 ttu">Variants</h5>
          <FieldArray
            name="variants"
            validateOnChange={false}
            render={arrayHelpers => (
              <div>
                {(variants || []).map((orderVariant, idx) => (
                  <div key={idx} className={styles.step}>
                    <div className="flex justify-end">
                      <div className="flex">
                        <IconTooltip
                          icon="delete-circle"
                          text="Remove variant"
                          className="ml3 stroke-brand-red"
                          onClick={() => arrayHelpers.remove(idx)}
                        />
                      </div>
                    </div>
                    <div className="mh3">
                      <div className="mb3 flex">
                        <div className="mr3 flex-grow-1">
                          <Label>Name</Label>
                          <FormikInputField
                            name={`variants.${idx}.name`}
                            invalid={hasError(errors, idx, 'variants', 'name')}
                          />
                          {renderError(errors, idx, 'variants', 'name')}
                        </div>
                        <div className="w4">
                          <Label>Xero item code</Label>
                          <FormikInputField
                            disabled={orderVariant.id && orderVariant.code}
                            name={`variants.${idx}.code`}
                            invalid={hasError(errors, idx, 'variants', 'code')}
                          />
                          {renderError(errors, idx, 'variants', 'code')}
                        </div>
                      </div>
                      <div className="mb3">
                        <Label>Description</Label>
                        <FormikInputField type="textarea" name={`variants.${idx}.description`} />
                      </div>
                      <div className="flex flex-column flex-row-ns">
                        <div className="mb3 flex-grow-1 mr3">
                          <Label>Vessel type</Label>
                          <FormikInputField
                            type="select"
                            name={`variants.${idx}.vesselTypeId`}
                            invalid={hasError(errors, idx, 'variants', 'vesselTypeId')}
                          >
                            <VesselTypeOptions />
                          </FormikInputField>
                          {renderError(errors, idx, 'variants', 'vesselTypeId')}
                        </div>
                        <div className="mb3 mr3">
                          <Label>Packaging cost ($)</Label>
                          <FormikInputField
                            name={`variants.${idx}.packagingCost`}
                            step=".01"
                            type="number"
                            invalid={!!errors.packagingCost}
                          />
                          {renderError(errors, idx, 'variants', 'packagingCost')}
                        </div>
                        <div className="mb3">
                          <Label>Tax per litre ($)</Label>
                          <FormikInputField
                            name={`variants.${idx}.taxPerLitre`}
                            step=".01"
                            type="number"
                            invalid={!!errors.taxPerLitre}
                          />
                          {renderError(errors, idx, 'variants', 'taxPerLitre')}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
                <div className="tr">
                  <AddButton color="primary" onClick={() => arrayHelpers.push({})}>
                    Add another variant
                  </AddButton>
                </div>
              </div>
            )}
          />
        </div>
      </FormLayout>

      <div className="flex justify-end mt4">
        <SubmissionButton color="primary" type="submit" isSubmitting={isSubmitting}>
          Save
        </SubmissionButton>
      </div>
    </form>
  );
};

export default ProductForm;



// WEBPACK FOOTER //
// ./src/components/productForm/productForm.jsx